import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    Verified as ConductIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintConductCertificatePage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const currentYear = new Date().getFullYear();
    const todayStr = new Date().toISOString().slice(0, 10);
    const startOfYear = `${currentYear}-09-01`;

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>(students[0]?.id ?? 'blank');
    const [startDate, setStartDate] = useState<string>(startOfYear);
    const [endDate, setEndDate] = useState<string>(todayStr);

    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const agencyName = user?.dosLib || user?.agency || 'BLOBTIC ACADEMY';
    const city = user?.dosAdr1 ? user.dosAdr1.split(',').pop()?.trim() || 'Alger' : 'Alger';

    const certNumber = selectedStudent
        ? `CBC/${currentYear}/${String(selectedStudent.id).padStart(4, '0')}`
        : `CBC/${currentYear}/0000`;

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '...................................................';
    const dob = selectedStudent?.date_of_birth ? `${selectedStudent.date_of_birth} à ${selectedStudent.address || city}` : '......./......./...............';
    const matricule = selectedStudent ? (selectedStudent.code || selectedStudent.STD_CODE || String(selectedStudent.id)) : '..................';
    const classLabel = studentGroup || '..................';

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الحقل' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'رقم الشهادة' : language === 'en' ? 'Certificate N°' : 'N° Certificat', certNumber],
            [language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom', studentFullName],
            [language === 'ar' ? 'تاريخ ومكان الميلاد' : language === 'en' ? 'Date & Place of birth' : 'Né(e) le et à', dob],
            [language === 'ar' ? 'الرقم الترتيبي' : language === 'en' ? 'Matricule' : 'N° matricule', matricule],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe', classLabel],
            [language === 'ar' ? 'الفترة من' : language === 'en' ? 'From' : 'Du', startDate],
            [language === 'ar' ? 'إلى' : language === 'en' ? 'To' : 'Au', endDate]
        ];
        exportToExcel(`Bonne_Conduite_${matricule}`, 'BonneConduite', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'شهادة حسن السيرة والسلوك' : language === 'en' ? 'Good Conduct Certificate' : 'Certificat de bonne conduite';
        exportToWord(`Bonne_Conduite_${matricule}`, title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '7px 10px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار التلميذ' : language === 'en' ? 'Select Student' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— استمارة فارغة —' : language === 'en' ? '— Blank —' : '— Vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} sm={3} md={3}>
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'من' : language === 'en' ? 'From' : 'Du'}
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={3} md={3}>
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'إلى' : language === 'en' ? 'To' : 'Au'}
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'شهادة حسن السيرة والسلوك' : language === 'en' ? 'Good Conduct Certificate' : 'Certificat de bonne conduite'}
            icon={<ConductIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 800, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'شهادة حسن السيرة والسلوك' : language === 'en' ? 'GOOD CONDUCT CERTIFICATE' : 'CERTIFICAT DE BONNE CONDUITE'}
                    subtitle={`N° ${certNumber}`}
                    showBorderBottom
                />

                <Box sx={{ my: 3 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 25 }}>
                        <tbody>
                            <tr>
                                <td style={cellStyle(240, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(240, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'تاريخ ومكان الميلاد' : language === 'en' ? 'Date & Place of birth' : 'Né(e) le et à'}
                                </td>
                                <td style={cellStyle()}>{dob}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(240, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'الرقم الترتيبي' : language === 'en' ? 'Matricule' : 'N° matricule'}
                                </td>
                                <td style={cellStyle()}>{matricule}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(240, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>{classLabel}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Attestation Body */}
                    <div style={{ fontSize: 13, lineHeight: 1.8, textAlign: 'justify', marginBottom: 35 }}>
                        {language === 'ar' ? (
                            <>
                                أشهد أنا الممضي(ة) أسفله، مدير(ة) مؤسسة <strong>{agencyName}</strong>، أن التلميذ(ة) <strong>{studentFullName}</strong> قد تمدرس(ت) بهذه المؤسسة من <strong>{startDate}</strong> إلى <strong>{endDate}</strong>، وكان(ت) حسن(ة) السيرة والسلوك والمواظبة طيلة مدة تواجده(ا) بها.
                                <br /><br />
                                سُلّمت له(ا) هذه الشهادة للإدلاء بها واستعمالها فيما يسمح به القانون.
                            </>
                        ) : language === 'en' ? (
                            <>
                                I, the undersigned Director of the institution <strong>{agencyName}</strong>, hereby certify that the student <strong>{studentFullName}</strong> attended our institution from <strong>{startDate}</strong> to <strong>{endDate}</strong>, and demonstrated exemplary conduct, discipline, and regular attendance throughout their presence.
                                <br /><br />
                                This certificate is issued to serve for all legal intents and purposes.
                            </>
                        ) : (
                            <>
                                Je soussigné(e), directeur(trice) de l'établissement <strong>{agencyName}</strong>, certifie que l'apprenant désigné(e) ci-dessus a été scolarisé(e) dans notre établissement du <strong>{startDate}</strong> au <strong>{endDate}</strong>, et qu'il(elle) y a été de bonne conduite durant toute sa présence.
                                <br /><br />
                                Le présent certificat lui est délivré pour servir et valoir ce que de droit.
                            </>
                        )}
                    </div>

                    <div style={{ fontSize: 12, marginBottom: 40, textAlign: isRtl ? 'left' : 'right' }}>
                        {language === 'ar' ? `حرر بـ ${city} في ${todayStr}` : language === 'en' ? `Done in ${city}, on ${todayStr}` : `Fait à ${city}, le ${todayStr}`}
                    </div>

                    {/* Signatures */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none' }} />
                                <td style={{ width: '50%', border: 'none', textAlign: 'center' }}>
                                    <strong>
                                        {language === 'ar' ? 'المدير (الختم والتوقيع)' : language === 'en' ? 'Director (Stamp and Signature)' : 'Le directeur (cachet et signature)'}
                                    </strong>
                                    <div style={{ height: 90 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '80%', margin: '0 auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 40, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintConductCertificatePage;
